import { useMemo, useState } from "react";
import { Flame, KeyRound, Loader2, LogIn, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardNote, CardTitle } from "@/components/ui/card";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { EmptyState } from "@/components/empty-state";
import { Field, Input } from "@/components/ui/field";
import { Loading, LoadFailure } from "@/components/query-state";
import { StatusChip, type Tone } from "@/components/status";
import { TBody, TD, TH, THead, TR, Table } from "@/components/ui/table";
import { LiveLoginDialog } from "@/components/live-login";
import { byUrgency, sessionExpiry, type SessionUrgency } from "@/lib/health";
import { useBurnSessions, useCreateIdentity, useIdentities, useMintSession } from "@/lib/queries";
import type { IdentityHealth } from "@/lib/schemas";
import { formatDuration, formatRelative } from "@/lib/time";
import { useNow } from "@/lib/use-now";
import { cn } from "@/lib/cn";

const URGENCY_TONE: Record<SessionUrgency, Tone> = {
  gone: "danger",
  critical: "danger",
  warning: "warn",
  healthy: "ok",
  idle: "muted",
};

const URGENCY_LABEL: Record<SessionUrgency, string> = {
  gone: "no session",
  critical: "expiring now",
  warning: "expiring soon",
  healthy: "healthy",
  idle: "out of rotation",
};

const STATUS_TONE: Record<IdentityHealth["status"], Tone> = {
  active: "ok",
  // Not "danger": an identity waiting on a human is not damage, it is a
  // twenty-minute job somebody has to do. `blocked` is the damaged one --
  // Facebook wants a checkpoint cleared, and signing in again will not do it.
  needs_login: "warn",
  paused: "muted",
  blocked: "danger",
};

/**
 * Whether a live login is the thing to offer. `blocked` included: clearing a
 * checkpoint happens in the same view, starting from the session Facebook is
 * challenging.
 */
function needsHuman(identity: IdentityHealth): boolean {
  return identity.status === "needs_login" || identity.status === "blocked";
}

export function IdentitiesPage() {
  const now = useNow();
  const identities = useIdentities();
  const burn = useBurnSessions();
  const mint = useMintSession();
  const createIdentity = useCreateIdentity();
  const [pending, setPending] = useState<IdentityHealth | null>(null);
  /** The identity whose live browser is on screen, or null. One at a time. */
  const [loggingIn, setLoggingIn] = useState<IdentityHealth | null>(null);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [reason, setReason] = useState("");
  const [result, setResult] = useState<string | null>(null);
  /**
   * Which identity is minting. Held here rather than read off the mutation
   * because a mutation has one `isPending` for the whole table, and the
   * operator needs to see WHICH row is busy -- for up to ninety seconds.
   */
  const [minting, setMinting] = useState<string | null>(null);

  const rows = useMemo(() => {
    return (identities.data ?? [])
      .map((identity) => ({ identity, expiry: sessionExpiry(identity, now) }))
      .sort((a, b) => byUrgency(a.expiry, b.expiry) || a.identity.label.localeCompare(b.identity.label));
  }, [identities.data, now]);

  const needAttention = rows.filter(
    (row) => row.expiry.urgency === "critical" || row.expiry.urgency === "gone",
  ).length;

  function confirmBurn() {
    if (pending === null) return;
    const label = pending.label;
    burn.mutate(
      { identityId: pending.id, reason: reason.trim() === "" ? undefined : reason.trim() },
      {
        onSuccess: ({ burned }) => setResult(`Burned ${burned} session(s) for ${label}.`),
        onError: (error) => setResult(`Could not burn ${label}: ${error.message}`),
      },
    );
    setPending(null);
    setReason("");
  }

  function startMint(identity: IdentityHealth) {
    setResult(null);
    setMinting(identity.id);
    mint.mutate(identity.id, {
      onSuccess: ({ minted }) =>
        setResult(`Minted for ${identity.label}. Good until ${minted.expiresAt.toLocaleString()}.`),
      // The message is the server's, and the server's is the minter's: "the
      // browser profile is not logged in" is a two-minute fix, and an hour of
      // guessing if it is swallowed for a generic failure line.
      onError: (error) => setResult(`Could not mint for ${identity.label}: ${error.message}`),
      onSettled: () => setMinting(null),
    });
  }

  function confirmAdd() {
    const label = newLabel.trim();
    if (label === "") return;
    setResult(null);
    createIdentity.mutate(label, {
      onSuccess: ({ identity }) => setResult(`Added ${identity.label}. Log in next.`),
      onError: (error) => setResult(`Could not add ${label}: ${error.message}`),
      onSettled: () => {
        setAdding(false);
        setNewLabel("");
      },
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-base font-semibold">Identities</h1>
        <span className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setResult(null);
              setAdding(true);
            }}
          >
            <Plus aria-hidden="true" className="h-3.5 w-3.5" />
            New identity
          </Button>
        </span>
      </div>

      {identities.isPending ? <Loading what="identities" /> : null}
      {identities.error !== null ? (
        <LoadFailure
          what="identities"
          error={identities.error}
          onRetry={() => void identities.refetch()}
        />
      ) : null}

      {/* Announced rather than drawn as a banner: an operator arriving at this
          page is already looking at the table, and a second red box above it
          only pushes the rows down. */}
      <p role="status" aria-live="polite" className="sr-only">
        {needAttention > 0 ? `${needAttention} identities need attention.` : ""}
        {/* Announced, because a screen reader gets no spinner: ninety
            seconds of silence is indistinguishable from a dead button. */}
        {minting !== null ? "Minting. This takes up to ninety seconds." : ""}
        {result ?? ""}
      </p>

      {result !== null ? (
        <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm">{result}</p>
      ) : null}

      {identities.data !== undefined && rows.length === 0 ? (
        <Card>
          <EmptyState title="No identities yet.">
            Press <span className="font-medium">New identity</span>, then{" "}
            <span className="font-medium">Log in</span>.
          </EmptyState>
        </Card>
      ) : null}

      {rows.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{rows.length} identities</CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>Identity</TH>
                  <TH>Expires in</TH>
                  <TH className="text-right">Live</TH>
                  <TH className="text-right">Fails</TH>
                  <TH>Status</TH>
                  <TH>Last minted</TH>
                  <TH>Last used</TH>
                  <TH><span className="sr-only">Actions</span></TH>
                </TR>
              </THead>
              <TBody>
                {rows.map(({ identity, expiry }) => (
                  <TR
                    key={identity.id}
                    data-urgency={expiry.urgency}
                    className={cn(
                      expiry.urgency === "critical" || expiry.urgency === "gone"
                        ? "bg-danger/5"
                        : undefined,
                    )}
                  >
                    <TD className="font-medium">{identity.label}</TD>

                    {/* Sized to be read from across a desk, because the state
                        it renders in the worst case -- "no live session" --
                        is the one nobody should have to go looking for. The
                        number itself is a backstop countdown, not a promise:
                        Facebook can reject a session with days left on it. */}
                    <TD
                      data-numeric
                      data-testid={`expiry-${identity.label}`}
                      data-urgency={expiry.urgency}
                      className="whitespace-nowrap"
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className={cn(
                            "text-xl font-semibold leading-none",
                            expiry.urgency === "critical" || expiry.urgency === "gone"
                              ? "text-danger"
                              : expiry.urgency === "warning"
                                ? "text-warn"
                                : "text-foreground",
                          )}
                        >
                          {expiry.msRemaining === null ? "-" : formatDuration(expiry.msRemaining)}
                        </span>
                        <StatusChip
                          tone={URGENCY_TONE[expiry.urgency]}
                          icon={expiry.urgency === "idle" ? "pause" : "default"}
                        >
                          {URGENCY_LABEL[expiry.urgency]}
                        </StatusChip>
                      </span>
                    </TD>

                    <TD data-numeric className="text-right">
                      {identity.liveSessions}
                    </TD>
                    <TD data-numeric className={cn("text-right", identity.consecutiveFailures > 0 && "text-warn")}>
                      {identity.consecutiveFailures}
                    </TD>
                    <TD>
                      <StatusChip
                        tone={STATUS_TONE[identity.status]}
                        icon={identity.status === "paused" ? "pause" : "default"}
                      >
                        {identity.status}
                      </StatusChip>
                    </TD>
                    <TD
                      data-numeric
                      data-testid={`minted-${identity.label}`}
                      className="whitespace-nowrap text-muted-foreground"
                    >
                      <span className="flex items-center gap-2">
                        {formatRelative(identity.lastMintedAt, now)}
                        {/* "tried and failed" is a different fact from "never
                            tried", and for an hour the dashboard rendered both
                            as `never`. The count is here because one failure
                            is a blip and six is a logged-out profile. */}
                        {identity.mintFailures > 0 ? (
                          <StatusChip tone="warn">
                            {identity.mintFailures} failed
                          </StatusChip>
                        ) : null}
                      </span>
                      {identity.lastMintError !== null ? (
                        <span className="block max-w-56 truncate text-xs text-warn" title={identity.lastMintError}>
                          {identity.lastMintError}
                        </span>
                      ) : null}
                    </TD>
                    <TD data-numeric className="whitespace-nowrap text-muted-foreground">
                      {formatRelative(identity.lastUsedAt, now)}
                    </TD>
                    <TD className="text-right">
                      <span className="flex justify-end gap-2">
                        {/*
                          No confirmation, unlike Burn: minting adds a session
                          and destroys nothing, and during an outage a second
                          click is a second lost minute. It does BLOCK for up
                          to ninety seconds while a browser starts, so the
                          button says so rather than looking frozen -- and it
                          is disabled meanwhile, because a second launch
                          against one profile fails rather than helping.
                        */}
                        {/*
                          Offered instead of Mint, not beside it, where there
                          is no login to mint from: a Mint button on a
                          `needs_login` row is a button whose only outcome is
                          the error the row is already showing.
                        */}
                        <Button
                          variant="outline"
                          size="sm"
                          aria-label={`Log in as ${identity.label}`}
                          onClick={() => {
                            setResult(null);
                            setLoggingIn(identity);
                          }}
                        >
                          <LogIn aria-hidden="true" className="h-3.5 w-3.5" />
                          Log in
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          aria-label={`Mint a session for ${identity.label}`}
                          disabled={minting !== null || needsHuman(identity)}
                          onClick={() => startMint(identity)}
                        >
                          {minting === identity.id ? (
                            <>
                              <Loader2
                                aria-hidden="true"
                                className="h-3.5 w-3.5 animate-spin"
                              />
                              Minting…
                            </>
                          ) : (
                            <>
                              <KeyRound aria-hidden="true" className="h-3.5 w-3.5" />
                              Mint
                            </>
                          )}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          aria-label={`Burn sessions for ${identity.label}`}
                          disabled={identity.liveSessions === 0 || burn.isPending}
                          onClick={() => {
                            setResult(null);
                            setPending(identity);
                          }}
                        >
                          <Flame aria-hidden="true" className="h-3.5 w-3.5" />
                          Burn
                        </Button>
                      </span>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardBody>
        </Card>
      ) : null}

      <LiveLoginDialog
        identity={loggingIn}
        onClose={() => {
          setLoggingIn(null);
          void identities.refetch();
        }}
      />

      {/*
        No confirmation: adding an identity creates an empty row with no
        credential in it, and the row is deletable in the sense that it can
        be paused. It is the least dangerous thing on this page.
      */}
      <Dialog
        open={adding}
        onOpenChange={(open) => {
          if (!open) {
            setAdding(false);
            setNewLabel("");
          }
        }}
      >
        <DialogContent>
          <DialogTitle>New identity</DialogTitle>
          <DialogDescription>A name. You log the account in afterwards.</DialogDescription>
          <div className="mt-4 flex flex-col gap-4">
            <Field
              label="Label"
              htmlFor="identity-label"
              hint="Lowercase letters, digits and hyphens."
            >
              <Input
                id="identity-label"
                value={newLabel}
                maxLength={40}
                placeholder="uk-2"
                onChange={(event) => setNewLabel(event.target.value)}
              />
            </Field>
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button variant="outline" size="sm">
                  Cancel
                </Button>
              </DialogClose>
              <Button size="sm" disabled={createIdentity.isPending} onClick={confirmAdd}>
                Add identity
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/*
        A confirmation, because this destroys credentials that are working
        right now: the fleet cannot poll with this identity again until a mint
        succeeds, and a mint is the step that fails.
      */}
      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null);
            setReason("");
          }
        }}
      >
        <DialogContent role="alertdialog">
          <DialogTitle>Burn {pending?.liveSessions ?? 0} live sessions?</DialogTitle>
          <DialogDescription>
            {pending?.label} cannot collect again until a mint succeeds. This cannot be undone.
          </DialogDescription>
          <div className="mt-4 flex flex-col gap-4">
            <Field
              label="Reason (optional)"
              htmlFor="burn-reason"
              hint="A short note, kept with the burn."
            >
              <Input
                id="burn-reason"
                value={reason}
                maxLength={200}
                placeholder="checkpoint email"
                onChange={(event) => setReason(event.target.value)}
              />
            </Field>
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button variant="outline" size="sm">
                  Cancel
                </Button>
              </DialogClose>
              <Button variant="danger" size="sm" onClick={confirmBurn}>
                Burn sessions
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
